import React, { useState, useRef, useEffect } from "react";
import "./AuditTrail.scss";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import TrailActionMenu from "./TrailActionMenu";
import { useTicketDynamicFieldsQuery } from "../../../Services/ticket/CreateTicket";

interface IProps {
  auditTrailData: any[];
  auditTrailForwardedData: any;
  tickets: any;
}

const Trail: React.FC<IProps> = ({
  auditTrailData,
  auditTrailForwardedData,
  tickets,
}) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [dynamicFieldData, setDynamicFieldData] = useState<any>([]);
  const { data: ticketDynamicFields } = useTicketDynamicFieldsQuery({});

  console.log(auditTrailForwardedData, "auditTrailForwardedData");

  const toggleMenu = (index: number) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Helper for parsing values
  const getParsedValue = (val: any) => {
    if (!val) return "-";
    try {
      const parsed = typeof val === "string" ? JSON.parse(val) : val;
      if (
        !parsed ||
        (typeof parsed === "object" && Object.keys(parsed).length === 0)
      )
        return "-";

      // If it's an array, display as comma-separated values
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.join(', ');
      }

      // If it's an object with multiple entries, return JSX for line breaks
      if (typeof parsed === "object" && Object.keys(parsed).length > 0) {
        return Object.entries(parsed).map(([key, v], idx) => (
          <span key={idx}>
            {key}: {
              // If the value is an array, display as comma-separated values
              Array.isArray(v) 
                ? v.join(', ')
                : String(v)
            }
            {idx < Object.entries(parsed).length - 1 && <br />}
          </span>
        ));
      }

      return String(parsed);
    } catch {
      return val;
    }
  };

  // Helper for CSV export
  const getParsedValueString = (val: any, forCsv = false) => {
    if (!val) return "-";
    try {
      const parsed = typeof val === "string" ? JSON.parse(val) : val;
      if (
        !parsed ||
        (typeof parsed === "object" && Object.keys(parsed).length === 0)
      )
        return "-";
      const str = Object.entries(parsed)
        .map(([key, v]) => `${key}: ${v}`)
        .join("\n");
      if (forCsv) {
        const safe = str.replace(/"/g, '""');
        return `"${safe}"`;
      }
      return str;
    } catch {
      return val;
    }
  };

  const downloadCSV = () => {
    if (auditTrailData.length === 0) return;

    const headers = [
      "Date",
      "Time",
      "Updates",
      "Previous",
      "New",
      "Assignee",
      "Assigned By",
     
    ];

    const quoteCSV = (text: string) => {
      if (typeof text !== "string") text = String(text ?? "");
      if (/[,"\s]/.test(text)) {
        return '"' + text.replace(/"/g, '""') + '"';
      }
      return text;
    };

    const csvContent = [
      headers.map(quoteCSV).join(","),
      ...auditTrailData.map((row) =>
        [
          quoteCSV(formatDate(row.created_date)),
          quoteCSV(formatTime(row.created)),
          quoteCSV(row.ticket_activity || "N/A"),
          quoteCSV(
            getParsedValueString(row.previous_value ?? row.prev_value, true),
          ),
          quoteCSV(getParsedValueString(row.new_value, true)),
          quoteCSV(row.new_assignee || "N/A"),
          quoteCSV(row.assigned_by || "N/A"),
     
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "audit_trail_data.csv");
  };

  const downloadExcel = () => {
    if (auditTrailData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(
      auditTrailData.map((row) => ({
        Date: formatDate(row.created_date),
        Time: formatTime(row.created),
        Updates: row.ticket_activity || "N/A",
        Previous: getParsedValueString(row.previous_value ?? row.prev_value),
        New: getParsedValueString(row.new_value),
        Assignee: row.new_assignee || "N/A",
        AssignedBy: row.assigned_by || "N/A",
       
      })),
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit Trail");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "audit_trail_data.xlsx");
  };

  const handleEdit = (id: any) => {
    console.log(`Edit clicked for ID: ${id}`);
  };

  const handleDelete = (id: any) => {
    console.log(`Delete clicked for ID: ${id}`);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (tickets?.action?.disposition?.workflow?.id) {
      const filterDataById = ticketDynamicFields?.filter(
        (name) =>
          name?.ticket_name === tickets?.action?.disposition?.workflow?.id,
      );
      setDynamicFieldData(filterDataById);
    }
  }, [ticketDynamicFields, tickets?.action?.disposition?.workflow?.id]);

  // Get icon and badge type based on activity
  const getActivityType = (activity: string) => {
    const activityLower = activity?.toLowerCase() || "";

    console.log(activityLower, "activityLower");

    if (
      (activityLower.includes("created") && !activityLower.includes("child ticket")) ||
      activityLower.includes("initialized")
    ) {
      return {
        icon: "bi-plus-circle-fill",
        iconClass: "atl-icon-created",
        badge: "atl-badge-created",
        badgeText: "CREATED",
      };
    }
    else if (activityLower.includes("marked actionable")) {
      return {
        icon: "bi-check-circle-fill",
        iconClass: "atl-icon-actionable",
        badge: "atl-badge-actionable",
        badgeText: "MARKED ACTIONABLE",
      };
    }
    else if (activityLower.includes("non actionable")){
      return {
        icon: "bi-x-circle-fill",
        iconClass: "atl-icon-nonactionable",
        badge: "atl-badge-nonactionable",
        badgeText: "NON ACTIONABLE",
      };
    } else if (activityLower.includes("external note")) {
      return {
        icon: "bi-chat-right-text-fill",
        iconClass: "atl-icon-note-external",
        badge: "atl-badge-updated",
        badgeText: "EXTERNAL NOTE",
      };
    } else if (activityLower.includes("internal note")) {
      return {
        icon: "bi-chat-left-text-fill",
        iconClass: "atl-icon-note-internal",
        badge: "atl-badge-updated",
        badgeText: "INTERNAL NOTE",
      };
    } else if (activityLower.includes("assignee")) {
      return {
        icon: "bi-person-fill",
        iconClass: "atl-icon-assigned",
        badge: "atl-badge-assigned",
        badgeText: "ASSIGNED",
      };
    } else if (activityLower.includes("status")) {
      return {
        icon: "bi-arrow-repeat",
        iconClass: "atl-icon-status",
        badge: "atl-badge-updated",
        badgeText: "STATUS CHANGED",
      };
    } else if (activityLower.includes("attachment")) {
      return {
        icon: "bi-paperclip",
        iconClass: "atl-icon-attachment",
        badge: "atl-badge-attachment",
        badgeText: "ATTACHMENT",
      };
    } else if (activityLower.includes("information updated")) {
      return {
        icon: "bi-pencil-square",
        iconClass: "atl-icon-info",
        badge: "atl-badge-updated",
        badgeText: "INFO UPDATED",
      };
    } else if (activityLower.includes("email")) {
      return {
        icon: "bi-envelope-fill",
        iconClass: "atl-icon-email",
        badge: "atl-badge-updated",
        badgeText: "EMAIL",
      };
    } else if (activityLower.includes("comment")) {
      return {
        icon: "bi-chat-fill",
        iconClass: "atl-icon-comment",
        badge: "atl-badge-updated",
        badgeText: "COMMENT",
      };
    } else if (
      activityLower.includes("deleted") ||
      activityLower.includes("removed")
    ) {
      return {
        icon: "bi-trash-fill",
        iconClass: "atl-icon-deleted",
        badge: "atl-badge-deleted",
        badgeText: "DELETED",
      };
    } else if (activityLower.includes("child ticket")) {
      return {
        icon: "bi-diagram-3-fill",
        iconClass: "atl-icon-child-ticket",
        badge: "atl-badge-created",
        badgeText: "CHILD TICKET",
      };
    } else if (
      activityLower.includes("update") ||
      activityLower.includes("change") ||
      activityLower.includes("priority")
    ) {
      return {
        icon: "bi-arrow-repeat",
        iconClass: "atl-icon-update",
        badge: "atl-badge-updated",
        badgeText: "UPDATED",
      };
    } else {
      return {
        icon: "bi-circle-fill",
        iconClass: "atl-icon-default",
        badge: "atl-badge-updated",
        badgeText: "ACTIVITY",
      };
    }
  };

  // Add this helper function before the return statement
  const getTooltipText = (activity: string) => {
    const activityLower = activity?.toLowerCase() || "";
    if (activityLower.includes("non actionable")) return "Marked Non Actionable";
    if (activityLower.includes("created")) return "Ticket Created";
    if (activityLower.includes("external note")) return "External Note Added";
    if (activityLower.includes("internal note")) return "Internal Note Added";
    if (activityLower.includes("assignee")) return "Assignee Changed";
    if (activityLower.includes("status")) return "Status Updated";
    if (activityLower.includes("attachments")) return "Attachment Added";
    if (activityLower.includes("information updated"))
      return "Information Updated";
    if (activityLower.includes("email")) return "Email Activity";
    if (activityLower.includes("comment")) return "Comment Added";
    if (activityLower.includes("deleted")) return "Item Deleted";
    if (activityLower.includes("child ticket")) return "Child Ticket Created";
    return "Activity";
  };
  // Get user avatar initials
  const getUserInitials = (name: any) => {
    // Skip if name is an object
    if (typeof name === 'object' && name !== null) {
      return "";
    }
    
    if (!name || name === "N/A") return "S";
    console.log(name, "name");
    const parts = String(name)?.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return String(name).substring(0, 1).toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string, index: number) => {
    const colors = [
      "atl-avatar-green",
      "atl-avatar-blue",
      "atl-avatar-purple",
      "atl-avatar-gray",
    ];
    if (!name || name === "N/A") return "atl-avatar-gray";
    return colors[name.length % colors.length];
  };

  console.log(auditTrailData, "test");

  return (
    <div className="audit-trail-timeline-wrapper">
      <div className="audit-trail-timeline-header">
        <h2 className="audit-trail-timeline-title">Audit Trail</h2>
        <div className="audit-trail-export-actions">
          <button className="audit-trail-export-button" onClick={downloadCSV}>
            <i className="bi bi-file-earmark-text text-white"></i>
            Export CSV
          </button>
          <button className="audit-trail-export-button" onClick={downloadExcel}>
            <i className="bi bi-file-earmark-spreadsheet text-white"></i>
            Export Excel
          </button>
        </div>
      </div>

      {auditTrailData && auditTrailData?.length > 0 ? (
        <div className="audit-trail-timeline-items-container">
          {auditTrailData?.map((data, index) => {
            const activityType = getActivityType(data.ticket_activity);
            const formattedDate = formatDate(data.created_date);
            const formattedTime = formatTime(data.created);

            return (
              <div key={index} className="audit-trail-timeline-event">
                <div
                  className={`audit-trail-timeline-event-icon ${activityType.iconClass}`}
                  title={getTooltipText(data.ticket_activity)} // Fallback native tooltip
                  data-tooltip={getTooltipText(data.ticket_activity)}
                >
                  <i className={activityType.icon}></i>
                </div>

                <div className="audit-trail-timeline-event-card">
                  <div className="audit-trail-event-card-header">
                    <div className="audit-trail-event-timestamp">
                      {formattedDate} • {formattedTime}
                    </div>
                    <div
                      className={`audit-trail-event-status-badge ${activityType.badge}`}
                    >
                      {activityType.badgeText}
                    </div>
                  </div>

                  <div className="audit-trail-event-card-body">
                    {data.ticket_activity && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          {activityType.badgeText === "ASSIGNED"
                            ? "ACTION"
                            : activityType.badgeText === "CREATED"
                            ? "EVENT"
                            : "UPDATE TYPE"}
                        </div>
                        <div className="audit-trail-field-value">
                          {data?.ticket_activity}
                        </div>
                      </div>
                    )}

                    {(data?.previous_value || data?.prev_value) && activityType.badgeText !== "NON ACTIONABLE" && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          PREVIOUS{" "}
                          {activityType?.badgeText === "ASSIGNED"
                            ? "ASSIGNEE"
                            : "VALUE"}
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={activityType?.badgeText === "ASSIGNED" ? `audit-trail-user-avatar-circle ${getAvatarColor(
                              data.previous_value ?? data.prev_value,
                              index,
                            )}` : ""}
                          >
                            {activityType?.badgeText === "ASSIGNED" ? getUserInitials(data.previous_value ?? data.prev_value) : ""}
                          </div>
                          <div className="audit-trail-field-value">
                            {getParsedValue(
                              data?.previous_value ?? data?.prev_value,
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {data?.new_value && activityType.badgeText !== "NON ACTIONABLE" && activityType.badgeText !== "MARKED ACTIONABLE" && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          NEW{" "}
                          {activityType.badgeText === "ASSIGNED"
                            ? "ASSIGNEE"
                            : "VALUE"}
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={activityType?.badgeText === "ASSIGNED" ? `audit-trail-user-avatar-circle ${getAvatarColor(data.new_value, index)}` : ""}
                          >
                            {activityType?.badgeText === "ASSIGNED" ? getUserInitials(data.new_value) : ""}
                          </div>
                          <div
                            className={`audit-trail-field-value ${
                            String(data?.new_value || "")
                              .toLowerCase()
                              .includes("high")
                              ? ""
                              : ""
                          }`}
                          >
                            {getParsedValue(data.new_value)}
                          </div>
                        </div>
                      </div>
                    )}

                    {(data?.new_assignee && data?.new_assignee === "Unassigned" ) && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          NEW ASSIGNEE
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.new_assignee,
                              index,
                            )}`}
                          >
                            {getUserInitials(data.new_assignee)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.new_assignee}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.assigned_by && data.assigned_by !== "N/A" && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          {activityType.badgeText === "ASSIGNED"
                            ? "ASSIGNED BY"
                            : activityType.badgeText === "CREATED"
                            ? "LOGGED BY"
                            : "MODIFIED BY"}
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.assigned_by,
                              index + 1,
                            )}`}
                          >
                            {getUserInitials(data.assigned_by)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.assigned_by}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.child_ticket_id && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          CHILD TICKET ID
                        </div>
                        <div className="audit-trail-field-value audit-trail-ticket-id">
                          <i className="bi bi-ticket-detailed-fill"></i>
                          <span>{data.child_ticket_id}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="audit-trail-timeline-no-data">
          <div className="audit-trail-empty-icon">
            <i className="bi bi-clock-history"></i>
          </div>
          <div className="audit-trail-empty-heading">No Audit Trail Data</div>
          <div className="audit-trail-empty-description">
            There are no updates to display for this ticket.
          </div>
        </div>
      )}
    </div>
  );
};

export default Trail;